import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X, Sparkles, MapPin, Dumbbell, RefreshCw, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const Discover = () => {
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLock, setActionLock] = useState(false);
  const [matchModal, setMatchModal] = useState(null); // { other, match_id }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discover");
      setProfiles(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load profiles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const swipe = async (action) => {
    if (actionLock || profiles.length === 0) return;
    const top = profiles[0];
    setActionLock(true);
    try {
      const { data } = await api.post("/swipes", { target_id: top.id, action });
      setProfiles((p) => p.slice(1));
      if (data.matched && data.match_id) {
        setMatchModal({ other: top, match_id: data.match_id });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't process that.");
    } finally {
      setActionLock(false);
    }
  };

  const top = profiles[0];

  return (
    <div data-testid="discover-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-1">
            Discover
          </p>
          <h1 className="font-heading text-4xl md:text-5xl">Today's Picks</h1>
        </div>
        <button
          onClick={load}
          data-testid="discover-refresh"
          className="text-neutral-400 hover:text-[#C6FF00] transition-colors p-2"
          aria-label="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {loading ? (
        <Skeleton />
      ) : !top ? (
        <EmptyState onRefresh={load} />
      ) : (
        <div className="relative h-[560px] sm:h-[620px]" data-testid="discover-stack">
          {profiles.slice(0, 3).reverse().map((p, idx, arr) => {
            const isTop = idx === arr.length - 1;
            return (
              <div
                key={p.id}
                className={`absolute inset-0 rounded-3xl overflow-hidden border border-white/10 transition-all duration-500`}
                style={{
                  transform: `translateY(${(arr.length - 1 - idx) * 8}px) scale(${
                    1 - (arr.length - 1 - idx) * 0.02
                  })`,
                  zIndex: idx,
                  opacity: isTop ? 1 : 0.6,
                }}
              >
                <img
                  src={p.photo_url || "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900"}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />

                {p.is_boosted && (
                  <div
                    data-testid={`discover-boost-pill-${p.id}`}
                    className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] shadow-[0_0_24px_rgba(198,255,0,0.55)]"
                    title={`Set Score ${p.set_score?.score ?? 0} — consistently shows up`}
                  >
                    <ShieldCheck size={12} strokeWidth={2.5} />
                    Verified · Showed Up
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-heading text-4xl md:text-5xl text-white">
                      {p.name}
                      {p.age ? <span className="text-neutral-300">, {p.age}</span> : null}
                    </h2>
                  </div>
                  {p.location_city && (
                    <div className="flex items-center gap-1.5 text-xs text-neutral-300 mb-3">
                      <MapPin size={12} /> {p.location_city}
                      {p.gym ? <span className="text-neutral-500">· {p.gym}</span> : null}
                    </div>
                  )}
                  {p.bio && (
                    <p className="text-sm text-neutral-200 max-w-md line-clamp-3 mb-3">{p.bio}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {(p.training_disciplines || []).slice(0, 4).map((d) => (
                      <span
                        key={d}
                        className="px-3 py-1 rounded-full bg-[#C6FF00]/15 border border-[#C6FF00]/40 text-[10px] uppercase tracking-wider text-[#C6FF00] font-semibold"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {top && !loading && (
        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            data-testid="discover-pass-btn"
            disabled={actionLock}
            onClick={() => swipe("pass")}
            className="w-16 h-16 rounded-full border-2 border-white/20 bg-[#111] text-white hover:border-red-400 hover:text-red-400 flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50"
            aria-label="Pass"
          >
            <X size={28} />
          </button>
          <button
            data-testid="discover-like-btn"
            disabled={actionLock}
            onClick={() => swipe("like")}
            className="w-20 h-20 rounded-full bg-[#C6FF00] text-[#0A0A0A] flex items-center justify-center transition-all hover:scale-110 shadow-[0_0_30px_rgba(198,255,0,0.4)] disabled:opacity-50"
            aria-label="Like"
          >
            <Heart size={32} fill="currentColor" />
          </button>
        </div>
      )}

      {matchModal && (
        <MatchModal
          other={matchModal.other}
          onClose={() => setMatchModal(null)}
          onMessage={() => nav(`/app/chat/${matchModal.match_id}`)}
        />
      )}
    </div>
  );
};

const Skeleton = () => (
  <div className="h-[560px] rounded-3xl border border-white/5 bg-[#111] animate-pulse" />
);

const EmptyState = ({ onRefresh }) => (
  <div className="text-center py-20" data-testid="discover-empty">
    <Dumbbell className="text-[#C6FF00] mx-auto mb-4" size={36} />
    <h2 className="font-heading text-3xl mb-2">You've seen everyone for now.</h2>
    <p className="text-sm text-neutral-400 mb-6">Check back tomorrow — new lifters drop in daily.</p>
    <button
      onClick={onRefresh}
      className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-3 rounded-md"
    >
      Refresh
    </button>
  </div>
);

const MatchModal = ({ other, onClose, onMessage }) => (
  <div
    className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl flex items-center justify-center px-6 animate-in fade-in"
    data-testid="match-modal"
  >
    <div className="max-w-sm w-full text-center">
      <div className="w-24 h-24 mx-auto rounded-full bg-[#C6FF00] flex items-center justify-center mb-6 shadow-[0_0_60px_rgba(198,255,0,0.5)]">
        <Sparkles size={42} className="text-[#0A0A0A]" />
      </div>
      <h2 className="font-heading text-6xl text-white leading-none mb-2">IT'S A</h2>
      <h2 className="font-heading text-6xl text-[#C6FF00] leading-none mb-6">MATCH</h2>
      <p className="text-sm text-neutral-300 mb-1">You and {other.name} liked each other.</p>
      <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-8">
        72 hours to plan a session.
      </p>
      <div className="flex flex-col gap-3">
        <button
          onClick={onMessage}
          data-testid="match-modal-message-btn"
          className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-sm uppercase tracking-[0.25em] px-6 py-4 rounded-md hover:scale-105 transition-transform"
        >
          Send Message
        </button>
        <button
          onClick={onClose}
          data-testid="match-modal-keep-btn"
          className="text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white font-semibold py-2"
        >
          Keep swiping →
        </button>
      </div>
    </div>
  </div>
);

export default Discover;
